/**
 * 이메일 인증 로직 hook
 * 인증 코드 생성, 발송, 타이머, 검증 담당
 */

import { useState, useEffect } from "react";
import { mailAPI } from "../../auth/api/mail-api";
import { showAlert } from "@/shared/lib/alert";

export interface EmailVerificationState {
  code: string;
  timeLeft: number;
  isVerified: boolean;
  isLoading: boolean;
  error: string | null;
  failedAttempts: number; // 인증 실패 횟수
}

/**
 * 8자리 랜덤 숫자 코드 생성
 */
function generateVerificationCode(): string {
  return Math.floor(10000000 + Math.random() * 90000000).toString();
}

/**
 * useEmailVerification - 이메일 인증 로직 관리
 */
export function useEmailVerification() {
  const [verificationState, setVerificationState] =
    useState<EmailVerificationState>({
      code: "",
      timeLeft: 0,
      isVerified: false,
      isLoading: false,
      error: null,
      failedAttempts: 0,
    });

  // 타이머 로직
  useEffect(() => {
    if (verificationState.timeLeft <= 0) {
      return;
    }

    const timer = setInterval(() => {
      setVerificationState((prev) => ({
        ...prev,
        timeLeft: Math.max(0, prev.timeLeft - 1),
      }));
    }, 1000);

    return () => clearInterval(timer);
  }, [verificationState.timeLeft]);

  /**
   * 이메일 인증 코드 발송
   */
  const sendVerificationEmail = async (
    email: string,
    username: string
  ): Promise<boolean> => {
    if (!email) {
      setVerificationState((prev) => ({
        ...prev,
        error: "이메일을 입력해주세요.",
      }));
      return false;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setVerificationState((prev) => ({
        ...prev,
        error: "올바른 이메일 형식이 아닙니다.",
      }));
      return false;
    }

    setVerificationState((prev) => ({
      ...prev,
      isLoading: true,
      error: null,
    }));

    try {
      // 8자리 랜덤 코드 생성
      const code = generateVerificationCode();

      // 백엔드로 인증 코드 발송
      await mailAPI.sendVerificationEmail({
        email,
        username,
        code,
      });

      // 상태 업데이트: 코드 저장, 10분(600초) 타이머 시작
      setVerificationState((prev) => ({
        ...prev,
        code,
        timeLeft: 600, // 10분
        isVerified: false,
        isLoading: false,
        error: null,
        failedAttempts: 0, // 새 코드 발송 시 실패 횟수 초기화
      }));

      return true;
    } catch (error: any) {
      const errorMessage =
        error.message || "이메일 인증 코드 발송에 실패했습니다.";
      setVerificationState((prev) => ({
        ...prev,
        isLoading: false,
        error: errorMessage,
      }));
      showAlert(errorMessage);
      return false;
    }
  };

  /**
   * 사용자 입력 코드 검증
   */
  const verifyCode = (userInputCode: string): boolean => {
    if (!userInputCode) {
      setVerificationState((prev) => ({
        ...prev,
        error: "인증 코드를 입력해주세요.",
      }));
      return false;
    }

    if (verificationState.timeLeft <= 0) {
      setVerificationState((prev) => ({
        ...prev,
        error: "인증 시간이 만료되었습니다. 다시 시도해주세요.",
      }));
      showAlert("인증 시간이 만료되었습니다. 다시 시도해주세요.");
      return false;
    }

    if (userInputCode !== verificationState.code) {
      setVerificationState((prev) => ({
        ...prev,
        error: "인증 코드가 일치하지 않습니다.",
        failedAttempts: prev.failedAttempts + 1, // 실패 횟수 증가
      }));
      return false;
    }

    // 인증 성공
    setVerificationState((prev) => ({
      ...prev,
      isVerified: true,
      error: null,
    }));

    return true;
  };

  /**
   * 인증 상태 초기화
   */
  const reset = () => {
    setVerificationState({
      code: "",
      timeLeft: 0,
      isVerified: false,
      isLoading: false,
      error: null,
      failedAttempts: 0,
    });
  };

  /**
   * 포맷된 시간 반환 (MM:SS)
   */
  const formattedTime = () => {
    const minutes = Math.floor(verificationState.timeLeft / 60);
    const seconds = verificationState.timeLeft % 60;
    return `${minutes.toString().padStart(2, "0")}:${seconds
      .toString()
      .padStart(2, "0")}`;
  };

  return {
    ...verificationState,
    sendVerificationEmail,
    verifyCode,
    reset,
    formattedTime,
  };
}
